// src/services/indexedDB/index.ts
// 統一導出介面（保持向後相容）

// 所有 imports 必須在檔案頂部（ESLint: import/first）
import { TimeBox } from '../../types/timeBox';
import { Event } from '../../types/calendar-event';
import { Task, TaskUpdateData } from '../../types/task';
import { Capture } from '../../types/capture';
import { Objective, ObjectiveStatus, CreateObjectiveRequest } from '../../features/goal/types/objective';
import { timeBoxRepository } from './repositories/timeBoxRepository';
import { eventRepository } from './repositories/eventRepository';
import { taskRepository } from './repositories/taskRepository';
import { captureRepository } from './repositories/captureRepository';
import { objectiveRepository, LocalObjective } from '../../features/goal/services/indexedDB/objectiveRepository';
import { DB_NAME, DB_VERSION, STORE_NAMES } from './core/database';
import { SyncStatus, QueryOptions } from './core/types';

// 導出核心模組
export * from './core/database';
export * from './core/types';

// 導出 Repository 實例
export { timeBoxRepository } from './repositories/timeBoxRepository';
export { eventRepository } from './repositories/eventRepository';
export { taskRepository } from './repositories/taskRepository';
export { captureRepository } from './repositories/captureRepository';
export { objectiveRepository, type LocalObjective } from '../../features/goal/services/indexedDB/objectiveRepository';

// ============================================
// 向後相容層：匯出與舊 API 相同的函數
// ============================================

// 資料庫配置（保持命名相容）
export const dbName = DB_NAME;
export const dbVersion = DB_VERSION;
export const timeBoxStore = STORE_NAMES.TIME_BOXES;
export const eventStore = STORE_NAMES.EVENTS;
export const taskStore = STORE_NAMES.TASKS;
export const captureStore = STORE_NAMES.CAPTURES;
export const objectiveStore = STORE_NAMES.OBJECTIVES;
export const syncQueueStore = STORE_NAMES.SYNC_QUEUE;

// 類型別名（保持相容）
export type ObjectiveSyncStatus = SyncStatus;

// ============================================
// TimeBox 相關函數（向後相容）
// ============================================

export const saveTimeBoxes = (userId: string, timeBoxes: TimeBox[]): Promise<void> =>
  timeBoxRepository.saveMany(userId, timeBoxes);

export const getTimeBoxesByDate = (userId: string, date: Date): Promise<TimeBox[]> =>
  timeBoxRepository.getByDate(userId, date);

export const getTimeBoxById = (id: string): Promise<TimeBox | null> =>
  timeBoxRepository.getById(id);

export const getCurrentTimeBox = (userId: string): Promise<TimeBox | null> =>
  timeBoxRepository.getCurrent(userId);

export const getNextTimeBox = (userId: string): Promise<TimeBox | null> =>
  timeBoxRepository.getNext(userId);

export const updateTimeBoxStatus = (id: string, status: string): Promise<void> =>
  timeBoxRepository.updateStatus(id, status);

export const cleanupExpiredTimeBoxes = (userId: string, retentionDays: number = 30) =>
  timeBoxRepository.cleanupExpired(userId, { retentionDays });

export const getTimeBoxCacheStats = (userId: string) =>
  timeBoxRepository.getCacheStats(userId);

export const createTimeBoxLocal = (timeBox: TimeBox): Promise<void> =>
  timeBoxRepository.createLocal(timeBox);

export const updateTimeBoxLocal = (id: string, updates: Partial<TimeBox>): Promise<void> =>
  timeBoxRepository.updateLocal(id, updates);

export const deleteTimeBoxLocal = (id: string): Promise<void> =>
  timeBoxRepository.deleteLocal(id);

export const replaceTimeBoxTempId = (tempId: string, realId: string): Promise<void> =>
  timeBoxRepository.replaceTempId(tempId, realId);

export const isTimeBoxRecentlyDeleted = (id: string): boolean =>
  timeBoxRepository.isRecentlyDeleted(id);

// ============================================
// Event 相關函數（向後相容）
// ============================================

export const saveEvents = (userId: string, events: Event[]): Promise<void> =>
  eventRepository.saveMany(userId, events);

export const getEventsByDateLocal = (userId: string, date: Date): Promise<Event[]> =>
  eventRepository.getByDate(userId, date);

export const getEventByIdLocal = (id: string): Promise<Event | null> =>
  eventRepository.getById(id);

export const createEventLocal = (event: Event): Promise<void> =>
  eventRepository.createLocal(event);

export const updateEventLocal = (id: string, updates: Partial<Event>): Promise<void> =>
  eventRepository.updateLocal(id, updates);

export const deleteEventLocal = (id: string): Promise<void> =>
  eventRepository.deleteLocal(id);

export const cleanupExpiredEvents = (userId: string, retentionDays: number = 30) =>
  eventRepository.cleanupExpired(userId, { retentionDays });

export const getEventCacheStats = (userId: string) =>
  eventRepository.getCacheStats(userId);

export const isEventRecentlyDeleted = (id: string): boolean =>
  eventRepository.isRecentlyDeleted(id);

// ============================================
// Task 相關函數（向後相容）
// ============================================

export const saveTasks = (userId: string, tasks: Task[]): Promise<void> =>
  taskRepository.saveMany(userId, tasks);

export const getAllTasks = (userId: string): Promise<Task[]> =>
  taskRepository.getAll(userId);

export const getTaskById = (id: string): Promise<Task | null> =>
  taskRepository.getById(id);

export const getOpenTasks = (userId: string, limit: number = 20): Promise<Task[]> =>
  taskRepository.getOpen(userId, limit);

export const cleanupExpiredTasks = (userId: string, retentionDays: number = 30) =>
  taskRepository.cleanupExpired(userId, { retentionDays });

export const getTaskCacheStats = (userId: string) =>
  taskRepository.getCacheStats(userId);

// 統一命名：使用 Local 而非 InCache
export const updateTaskLocal = (id: string, updates: Partial<Task> | TaskUpdateData): Promise<void> =>
  // TaskUpdateData 允許 null（清空語義），base-repository.updateLocal 在執行時可正確處理
  taskRepository.updateLocal(id, updates as Partial<Task>);

export const deleteTaskLocal = (id: string): Promise<void> =>
  taskRepository.deleteLocal(id);

export const createTaskLocal = (task: Task): Promise<void> =>
  taskRepository.createLocal(task);

export const replaceTaskTempId = (tempId: string, realId: string): Promise<void> =>
  taskRepository.replaceTempId(tempId, realId);

// 特殊功能：回滾
export const revertTaskLocal = (taskId: string, originalTask: Task): Promise<void> =>
  taskRepository.revert(taskId, originalTask);

// 舊命名（向後相容）
export const updateTaskInCache = updateTaskLocal;
export const deleteTaskFromCache = deleteTaskLocal;
export const revertTaskInCache = revertTaskLocal;

// ============================================
// Capture 相關函數（向後相容）
// ============================================

export const saveCaptures = (userId: string, captures: Capture[]): Promise<void> =>
  captureRepository.saveMany(userId, captures);

// getAllCaptures 重載支持
export function getAllCaptures(
  userId: string,
  options?: {
    limit?: number;
    offset?: number;
    sortBy?: 'createdAt' | 'updatedAt';
    sortOrder?: 'asc' | 'desc';
  }
): Promise<Capture[]>;
export function getAllCaptures(userId: string): Promise<Capture[]>;
export function getAllCaptures(userId: string, options?: QueryOptions & {
  sortBy?: 'createdAt' | 'updatedAt';
  sortOrder?: 'asc' | 'desc';
}): Promise<Capture[]> {
  return captureRepository.getAll(userId, options);
}

export const getCaptureById = (id: string): Promise<Capture | null> =>
  captureRepository.getById(id);

export const getUnprocessedCaptures = (userId: string, limit: number = 20): Promise<Capture[]> =>
  captureRepository.getUnprocessed(userId, limit);

export const getCapturesByTimeBoxId = (userId: string, timeBoxId: string): Promise<Capture[]> =>
  captureRepository.getByTimeBoxId(userId, timeBoxId);

export const cleanupExpiredCaptures = (userId: string, retentionDays: number = 30) =>
  captureRepository.cleanupExpired(userId, { retentionDays });

export const getCaptureCacheStats = (userId: string) =>
  captureRepository.getCacheStats(userId);

export const createCaptureLocal = (capture: Capture): Promise<void> =>
  captureRepository.createLocal(capture);

export const updateCaptureLocal = (id: string, updates: Partial<Capture>): Promise<void> =>
  captureRepository.updateLocal(id, updates);

export const deleteCaptureLocal = (id: string): Promise<void> =>
  captureRepository.deleteLocal(id);

export const replaceTempId = (tempId: string, realId: string): Promise<void> =>
  captureRepository.replaceTempId(tempId, realId);

export const syncIndexedDBWithFirestore = (
  userId: string,
  firestoreIds: string[]
): Promise<{ synced: number; deleted: number }> =>
  captureRepository.syncWithFirestore(userId, firestoreIds);

// ============================================
// Objective 相關函數（向後相容）
// ============================================

export const toLocalObjective = (
  objective: Objective,
  syncStatus: SyncStatus = 'synced'
): LocalObjective => objectiveRepository.toLocal(objective, syncStatus);

export const fromLocalObjective = (local: LocalObjective): Objective =>
  objectiveRepository.fromLocal(local);

export const saveObjectives = (
  userId: string,
  objectives: Objective[],
  syncStatus: SyncStatus = 'synced'
): Promise<void> => objectiveRepository.saveManyWithSync(userId, objectives, syncStatus);

export const getAllObjectives = (
  userId: string,
  options?: {
    status?: ObjectiveStatus | ObjectiveStatus[];
    excludeArchived?: boolean;
  }
): Promise<LocalObjective[]> => objectiveRepository.getAllLocal(userId, options);

export const getObjectiveById = (id: string): Promise<LocalObjective | null> =>
  objectiveRepository.getLocalById(id);

/** @deprecated 使用 createObjectiveFromRequest 替代 */
export const createObjectiveLocal = (objective: Objective): Promise<LocalObjective> =>
  objectiveRepository.createLocal(objective);

export const createObjectiveFromRequest = (userId: string, request: CreateObjectiveRequest): Promise<LocalObjective> =>
  objectiveRepository.createFromRequest(userId, request);

export const updateObjectiveLocal = (id: string, updates: Partial<LocalObjective>): Promise<void> =>
  objectiveRepository.updateLocal(id, updates as Partial<Objective>);

export const deleteObjectiveLocal = (id: string): Promise<void> =>
  objectiveRepository.deleteLocal(id);

export const getPendingObjectives = (userId: string): Promise<LocalObjective[]> =>
  objectiveRepository.getPending(userId);

export const updateObjectiveSyncStatus = (id: string, syncStatus: SyncStatus, serverId?: string): Promise<void> =>
  objectiveRepository.updateSyncStatus(id, syncStatus, serverId);

export const getActiveObjectivesCount = (userId: string): Promise<number> =>
  objectiveRepository.getActiveCount(userId);

export const cleanupExpiredObjectives = (userId: string, retentionDays: number = 30) =>
  objectiveRepository.cleanupExpired(userId, { retentionDays });
